/**
 * ============================================================================
 * USER / TENANT SERVICE: WhatsApp Campaigns (Sheets 21, 22, 23, 24)
 * ============================================================================
 * Purpose:
 *   Handles WhatsApp broadcast campaign management (`tbl_campaign_master`),
 *   template selection, Excel recipient mapping, campaign execution,
 *   scheduled batches (`tbl_campaign_batches`), and delivery logs.
 *
 * Database Scope:
 *   Tenant Database (`digi_user_<id>`)
 * ============================================================================
 */

import api from "../../core/services/api";
import { createCrudService } from "../../core/services/crudServiceFactory";

const baseCampaignService = createCrudService("tenant/campaigns");

const campaignService = {
  ...baseCampaignService,

  /**
   * Retrieves broadcast campaigns list with live metrics from tbl_campaign_master
   */
  getCampaigns: (params = {}) => {
    return api.get("/tenant/campaigns", { params });
  },

  /**
   * Alias for retrieving all campaigns
   */
  getAll: (params = {}) => {
    return api.get("/tenant/campaigns", { params });
  },

  /**
   * Creates or saves a draft campaign
   */
  createCampaign: (data) => {
    return api.post("/tenant/campaigns", data);
  },

  /**
   * Pre-calculates country-wise campaign costs, deduct rates, and wallet balance check
   */
  calculateCost: (payload) => {
    return api.post("/tenant/campaigns/calculate-cost", payload);
  },

  /**
   * Retrieves approved templates ready for broadcast campaign selection
   */
  getUserTemplates: (params = {}) => {
    return api.get("/tenant/campaigns/templates", { params });
  },

  /**
   * Uploads and parses Excel / CSV file containing audience phone numbers & columns
   */
  uploadExcel: (formData) => {
    return api.post("/tenant/campaigns/upload-excel", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  /**
   * Uploads campaign header media file (image, video, document/PDF) from computer
   */
  uploadMedia: (formData) => {
    return api.post("/tenant/campaigns/upload-media", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  /**
   * Dispatches and sends campaign messages immediately
   */
  sendCampaign: (id, payload = {}) => {
    return api.post(`/tenant/campaigns/${id}/send`, payload);
  },

  /**
   * Retrieves delivery breakdown log for a campaign from tbl_general_campaign_details
   */
  getCampaignMessages: (id, params = {}) => {
    return api.get(`/tenant/campaigns/${id}/messages`, { params });
  },

  /**
   * Deletes a broadcast campaign and drops its MongoDB message collection
   */
  deleteCampaign: (id, params = {}) => {
    return api.delete(`/tenant/campaigns/${id}`, { params });
  },

  /**
   * Retries failed recipients for a campaign
   */
  retryFailed: (id, payload = {}) => {
    return api.post(`/tenant/campaigns/${id}/retry`, payload);
  },

  /**
   * Retrieves real-time delivery status for a campaign
   */
  getCampaignStatus: (id) => {
    return api.get(`/tenant/campaigns/${id}/status`);
  },

  /**
   * Retrieves batches for a campaign.
   */
  getBatches: (params = {}) => {
    return api.get("/tenant/campaign-batches", { params });
  },

  /**
   * Retrieves delivery details for general UI-launched campaigns.
   */
  getGeneralDetails: (params = {}) => {
    return api.get("/tenant/campaign-details/general", { params });
  },

  /**
   * Retrieves delivery details for programmatic API-launched campaigns.
   */
  getApiDetails: (params = {}) => {
    return api.get("/tenant/campaign-details/api", { params });
  },

  /**
   * Retrieves campaign batch and throttle configuration settings.
   */
  getBatchSettings: (params = {}) => {
    return api.get("/tenant/campaign-batch-settings", { params });
  },

  /**
   * Saves campaign batch and throttle configuration settings.
   */
  saveBatchSettings: (payload) => {
    return api.post("/tenant/campaign-batch-settings", payload);
  },

  /**
   * Cancels a running or pending campaign, halting new RabbitMQ jobs and safely marking pending recipients as CANCELLED.
   */
  cancelCampaign: (id) => {
    return api.post(`/tenant/campaigns/${id}/cancel`);
  },
};

export default campaignService;
